import { jwtDecode } from "jwt-decode";
import { getOktaAuthInstance } from "./auth";

/**
 * Get the current access token from Okta.
 */
export async function getAccessToken(): Promise<string | null> {
  const oktaAuthInstance = getOktaAuthInstance();

  if (!oktaAuthInstance) return null;

  const token = await oktaAuthInstance.tokenManager.get("accessToken");
  if (!token || !("accessToken" in token)) return null;

  return token.accessToken;
}

/**
 * Get the current ID token from Okta.
 */
export async function getIdToken(): Promise<string | null> {
  const oktaAuthInstance = getOktaAuthInstance();

  if (!oktaAuthInstance) return null;

  const token = await oktaAuthInstance.tokenManager.get("idToken");
  if (!token || !("idToken" in token)) return null;

  return token.idToken;
}

/**
 * Get the elation identifier from the ID token.
 */
export async function getElationIdentifier(): Promise<string | null> {
  const idToken = await getIdToken();
  if (!idToken) return null;

  try {
    const payload = jwtDecode(idToken) as { elation_identifier?: string };
    return payload.elation_identifier ?? null;
  } catch {
    return null;
  }
}

/**
 * Get practice IDs from the access token.
 */
export async function getPracticeIds(): Promise<number[] | null> {
  const accessToken = await getAccessToken();
  if (!accessToken) return null;

  try {
    const payload = jwtDecode(accessToken) as { practice_ids?: number[] };
    return payload.practice_ids ?? null;
  } catch {
    return null;
  }
}

/**
 * Clear all tokens and sign out.
 */
export async function signOut(postLogoutRedirectUri?: string): Promise<void> {
  const oktaAuthInstance = getOktaAuthInstance();

  if (oktaAuthInstance) {
    try {
      await oktaAuthInstance.signOut({ postLogoutRedirectUri });
    } catch (error) {
      console.error("Error signing out:", error);
    }
  }
}

/**
 * Check if the user is authenticated.
 */
export async function isAuthenticated(): Promise<boolean> {
  const oktaAuthInstance = getOktaAuthInstance();

  if (!oktaAuthInstance) return false;

  const token = await oktaAuthInstance.tokenManager.get("accessToken");
  if (!token || !("accessToken" in token)) return false;

  try {
    const { exp } = jwtDecode<{ exp: number }>(token.accessToken);
    return exp * 1000 > Date.now();
  } catch {
    return false;
  }
}
